import {
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  Input,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import FormErrorMessageWithSpacing from '../../../components/form-error-message';
import FormModal from '../../../components/FormModal';
import FormStack from '../../../components/form-stack';
import { translate } from '../../../i18n';
import { typography } from '../../../theme/tokens';
import { AccessControlConfigDto } from '../../../models/access-control/dto/access-control-config.dto';
import { useSetAccessControlCredentials } from '../hooks/useSetAccessControlCredentials';
import { CREDENTIAL_FIELDS, getProviderLabel } from '../utils/access-control.utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  /** The config row whose credentials are being set. */
  config: AccessControlConfigDto | null;
}

/**
 * Superadmin-only vendor-credentials editor (the API 403s everyone else).
 * Write-only by design: existing secrets are never fetched or displayed —
 * the form always starts blank and saving replaces the whole credentials
 * object. Providers with known keys (see CREDENTIAL_FIELDS) get typed
 * inputs; the rest fall back to a raw JSON editor.
 */
export default function AccessControlCredentialsModal({
  isOpen,
  onClose,
  organizationId,
  config,
}: Props) {
  const toast = useToast();
  const fields = useMemo(
    () => (config ? CREDENTIAL_FIELDS[config.provider] ?? [] : []),
    [config],
  );
  const [values, setValues] = useState<Record<string, string>>({});
  const [rawJson, setRawJson] = useState('{}');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Always blank on open — secrets are write-only, there is nothing to
    // pre-fill (the API never returns them).
    setValues({});
    setRawJson('{}');
    setError(null);
  }, [isOpen, config]);

  const { mutate, isLoading } = useSetAccessControlCredentials(organizationId, () => {
    toast({
      title: translate('access_control.credentials_saved' as any),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
    onClose();
  });

  const handleSubmit = () => {
    if (!config) return;

    let credentials: Record<string, unknown>;
    if (fields.length > 0) {
      const missing = fields.filter(
        (field) => field.required && !(values[field.key] || '').trim(),
      );
      if (missing.length > 0) {
        setError(
          `${translate('access_control.credentials_missing_fields' as any)}: ${missing
            .map((field) => field.key)
            .join(', ')}`,
        );
        return;
      }
      credentials = fields.reduce<Record<string, unknown>>((acc, field) => {
        const value = (values[field.key] || '').trim();
        if (value !== '') acc[field.key] = value;
        return acc;
      }, {});
    } else {
      try {
        credentials = JSON.parse(rawJson);
      } catch {
        setError(translate('access_control.credentials_invalid_json' as any));
        return;
      }
      if (
        typeof credentials !== 'object' ||
        credentials === null ||
        Array.isArray(credentials) ||
        Object.keys(credentials).length === 0
      ) {
        setError(translate('access_control.credentials_invalid_json' as any));
        return;
      }
    }
    setError(null);

    mutate(
      {
        provider: config.provider,
        locationId: config.locationId,
        credentials,
      },
      {
        onError: (mutationError: any) => {
          toast({
            title: translate('access_control.credentials_save_error' as any),
            description: mutationError?.message || '',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        },
      },
    );
  };

  if (!config) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={`${translate('access_control.set_credentials' as any)} — ${getProviderLabel(
        config.provider,
      )}`}
      onSubmit={handleSubmit}
      submitLabel={translate('common.save' as any)}
      cancelLabel={translate('common.cancel' as any)}
      isSubmitting={isLoading}
    >
      <FormStack density="section">
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {translate('access_control.credentials_write_only_warning' as any)}
        </Alert>

        {fields.length > 0 ? (
          fields.map((field) => (
            <FormControl key={field.key} isRequired={field.required}>
              <FormLabel {...typography.formLabel}>{field.key}</FormLabel>
              <Input
                type={field.secret ? 'password' : 'text'}
                autoComplete="off"
                value={values[field.key] || ''}
                onChange={(e) =>
                  setValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                }
              />
            </FormControl>
          ))
        ) : (
          <FormControl>
            <FormLabel {...typography.formLabel}>
              {translate('access_control.credentials_json' as any)}
            </FormLabel>
            <Textarea
              value={rawJson}
              onChange={(e) => setRawJson(e.target.value)}
              fontFamily="mono"
              fontSize="sm"
              rows={5}
            />
            <Text fontSize="xs" color="gray.500" mt={1}>
              {translate('access_control.credentials_json_helper' as any)}
            </Text>
          </FormControl>
        )}

        <FormControl isInvalid={!!error}>
          <FormErrorMessageWithSpacing title={error || ''} />
        </FormControl>
      </FormStack>
    </FormModal>
  );
}
